"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.StringAttribute_ = exports.StringAttribute = exports.$StringAttribute = void 0;
const ifThenElse_js_1 = require("../../utils/ifThenElse.js");
const overwrite_js_1 = require("../../utils/overwrite.js");
const update_js_1 = require("../../utils/update.js");
const attributeOptions_js_1 = require("../constants/attributeOptions.js");
const freeze_js_1 = require("./freeze.js");
/**
 * String attribute (warm)
 */
class $StringAttribute {
    constructor(state) {
        this[attributeOptions_js_1.$type] = 'string';
        this[attributeOptions_js_1.$state] = state;
    }
    /**
     * Tag attribute as required. Possible values are:
     * - `'atLeastOnce'` _(default)_: Required in PUTs, optional in UPDATEs
     * - `'never'`: Optional in PUTs and UPDATEs
     * - `'always'`: Required in PUTs and UPDATEs
     *
     * @param nextRequired RequiredOption
     */
    required(nextRequired = 'atLeastOnce') {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], { required: nextRequired }));
    }
    /**
     * Shorthand for `required('never')`
     */
    optional() {
        return this.required('never');
    }
    /**
     * Hide attribute after fetch commands and formatting
     */
    hidden(nextHidden = true) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], { hidden: nextHidden }));
    }
    /**
     * Tag attribute as a primary key attribute or linked to a primary attribute
     */
    key(nextKey = true) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], { key: nextKey, required: 'always' }));
    }
    /**
     * Rename attribute before save commands
     */
    savedAs(nextSavedAs) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], { savedAs: nextSavedAs }));
    }
    /**
     * Provide a finite list of possible values for attribute
     * (For typing reasons, enums are only available as attribute methods, not as input options)
     *
     * @param enum Possible values
     * @example
     * string().enum('foo', 'bar')
     */
    enum(...nextEnum) {
        return new $StringAttribute((0, update_js_1.update)(this[attributeOptions_js_1.$state], 'enum', nextEnum));
    }
    /**
     * Shorthand for `enum(constantValue).default(constantValue)`
     *
     * @param constantValue Constant value
     * @example
     * string().const('foo')
     */
    const(constant) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            enum: [constant],
            defaults: (0, ifThenElse_js_1.ifThenElse)(this[attributeOptions_js_1.$state].key, {
                key: constant,
                put: this[attributeOptions_js_1.$state].defaults.put,
                update: this[attributeOptions_js_1.$state].defaults.update
            }, {
                key: this[attributeOptions_js_1.$state].defaults.key,
                put: constant,
                update: this[attributeOptions_js_1.$state].defaults.update
            })
        }));
    }
    /**
     * Transform the attribute value in PUT commands OR Primary Key computing if attribute is tagged as key
     *
     * @param nextDefault `key/putAttributeInput | (() => key/putAttributeInput)`
     */
    transform(transform) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], { transform }));
    }
    /**
     * Provide a default value for attribute in Primary Key computing
     *
     * @param nextKeyDefault `keyAttributeInput | (() => keyAttributeInput)`
     */
    keyDefault(nextKeyDefault) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            defaults: {
                key: nextKeyDefault,
                put: this[attributeOptions_js_1.$state].defaults.put,
                update: this[attributeOptions_js_1.$state].defaults.update
            }
        }));
    }
    /**
     * Provide a default value for attribute in PUT commands
     *
     * @param nextPutDefault `putAttributeInput | (() => putAttributeInput)`
     */
    putDefault(nextPutDefault) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            defaults: {
                key: this[attributeOptions_js_1.$state].defaults.key,
                put: nextPutDefault,
                update: this[attributeOptions_js_1.$state].defaults.update
            }
        }));
    }
    /**
     * Provide a default value for attribute in UPDATE commands
     *
     * @param nextUpdateDefault `updateAttributeInput | (() => updateAttributeInput)`
     */
    updateDefault(nextUpdateDefault) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            defaults: {
                key: this[attributeOptions_js_1.$state].defaults.key,
                put: this[attributeOptions_js_1.$state].defaults.put,
                update: nextUpdateDefault
            }
        }));
    }
    /**
     * Provide a default value for attribute in PUT commands OR Primary Key computing if attribute is tagged as key
     *
     * @param nextDefault `key/putAttributeInput | (() => key/putAttributeInput)`
     */
    default(nextDefault) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            defaults: (0, ifThenElse_js_1.ifThenElse)(this[attributeOptions_js_1.$state].key, {
                key: nextDefault,
                put: this[attributeOptions_js_1.$state].defaults.put,
                update: this[attributeOptions_js_1.$state].defaults.update
            }, {
                key: this[attributeOptions_js_1.$state].defaults.key,
                put: nextDefault,
                update: this[attributeOptions_js_1.$state].defaults.update
            })
        }));
    }
    /**
     * Provide a **linked** default value for attribute in Primary Key computing
     *
     * @param nextKeyLink `keyAttributeInput | ((keyInput) => keyAttributeInput)`
     */
    keyLink(nextKeyLink) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            links: {
                key: nextKeyLink,
                put: this[attributeOptions_js_1.$state].links.put,
                update: this[attributeOptions_js_1.$state].links.update
            }
        }));
    }
    /**
     * Provide a **linked** default value for attribute in PUT commands
     *
     * @param nextPutLink `putAttributeInput | ((putItemInput) => putAttributeInput)`
     */
    putLink(nextPutLink) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            links: {
                key: this[attributeOptions_js_1.$state].links.key,
                put: nextPutLink,
                update: this[attributeOptions_js_1.$state].links.update
            }
        }));
    }
    /**
     * Provide a **linked** default value for attribute in UPDATE commands
     *
     * @param nextUpdateLink `unknown | ((updateItemInput) => updateAttributeInput)`
     */
    updateLink(nextUpdateLink) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            links: {
                key: this[attributeOptions_js_1.$state].links.key,
                put: this[attributeOptions_js_1.$state].links.put,
                update: nextUpdateLink
            }
        }));
    }
    /**
     * Provide a **linked** default value for attribute in PUT commands OR Primary Key computing if attribute is tagged as key
     *
     * @param nextLink `key/putAttributeInput | (() => key/putAttributeInput)`
     */
    link(nextLink) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            links: (0, ifThenElse_js_1.ifThenElse)(this[attributeOptions_js_1.$state].key, {
                key: nextLink,
                put: this[attributeOptions_js_1.$state].links.put,
                update: this[attributeOptions_js_1.$state].links.update
            }, {
                key: this[attributeOptions_js_1.$state].links.key,
                put: nextLink,
                update: this[attributeOptions_js_1.$state].links.update
            })
        }));
    }
    /**
     * Provide a custom validator for attribute in Primary Key computing
     *
     * @param nextKeyValidator `(keyAttributeInput) => boolean | string`
     */
    keyValidate(nextKeyValidator) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            validators: {
                key: nextKeyValidator,
                put: this[attributeOptions_js_1.$state].validators.put,
                update: this[attributeOptions_js_1.$state].validators.update
            }
        }));
    }
    /**
     * Provide a custom validator for attribute in PUT commands
     *
     * @param nextPutValidator `(putAttributeInput) => boolean | string`
     */
    putValidate(nextPutValidator) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            validators: {
                key: this[attributeOptions_js_1.$state].validators.key,
                put: nextPutValidator,
                update: this[attributeOptions_js_1.$state].validators.update
            }
        }));
    }
    /**
     * Provide a custom validator for attribute in UPDATE commands
     *
     * @param nextUpdateValidator `(updateAttributeInput) => boolean | string`
     */
    updateValidate(nextUpdateValidator) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            validators: {
                key: this[attributeOptions_js_1.$state].validators.key,
                put: this[attributeOptions_js_1.$state].validators.put,
                update: nextUpdateValidator
            }
        }));
    }
    /**
     * Provide a custom validator for attribute in PUT commands OR Primary Key computing if attribute is tagged as key
     *
     * @param nextValidator `(key/putAttributeInput) => boolean | string`
     */
    validate(nextValidator) {
        return new $StringAttribute((0, overwrite_js_1.overwrite)(this[attributeOptions_js_1.$state], {
            validators: (0, ifThenElse_js_1.ifThenElse)(
            /**
             * @debt type "remove this cast"
             */
            this[attributeOptions_js_1.$state].key, {
                key: nextValidator,
                put: this[attributeOptions_js_1.$state].validators.put,
                update: this[attributeOptions_js_1.$state].validators.update
            }, {
                key: this[attributeOptions_js_1.$state].validators.key,
                put: nextValidator,
                update: this[attributeOptions_js_1.$state].validators.update
            })
        }));
    }
    freeze(path) {
        return (0, freeze_js_1.freezeStringAttribute)(this[attributeOptions_js_1.$state], path);
    }
}
exports.$StringAttribute = $StringAttribute;
class StringAttribute {
    constructor({ path, ...state }) {
        this.type = 'string';
        this.path = path;
        this.required = state.required;
        this.hidden = state.hidden;
        this.key = state.key;
        this.savedAs = state.savedAs;
        this.enum = state.enum;
        this.transform = state.transform;
        this.defaults = state.defaults;
        this.links = state.links;
        this.validators = state.validators;
    }
}
exports.StringAttribute = StringAttribute;
class StringAttribute_ extends StringAttribute {
    clone(nextState = {}) {
        return new StringAttribute_({
            ...this,
            defaults: { ...this.defaults },
            links: { ...this.links },
            validators: { ...this.validators },
            ...nextState
        });
    }
    build(schemaAction) {
        return new schemaAction(this);
    }
}
exports.StringAttribute_ = StringAttribute_;
